import IOSSwitch from '@components/IOSSwitch';
import PopUpWarning from '@components/PopUpWarning';
import { pushError, pushSuccess } from '@components/toast';
import { geoZoneController } from '@controllers/index';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { IGetGeoZoneProps } from '@EcamModel/controllers/IGeoZoneHttpController';
import { GeoZoneCoordinates } from '@EcamModel/model';
import { CameraViewType, ViewPreset } from '@EcamModel/model/CameraView';
import usePopUp from '@hooks/usePopUp';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { getGeoZone, useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import { typeTextMap } from '@pages/cameras/hook/GeoZonesNavigateHandler';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import color from '@theme/Colors';
import { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import HistoriesPhotoCameraPTZ from '../photoHistories';

export default function ViewCameraPTZ({
    configView,
    listsViewPreset,
}: {
    configView: () => void;
    listsViewPreset: ViewPreset[];
}) {
    const {
        camera,
        scaleFullCameraPTZ,
        scaleNotFullCameraPTZ,
        navigate,
        selectedCameraView,
        setSelectedCameraView,
        handleMouseMove,
        isLoadingSave,
        selectedGeozone,
        handleUpdateCameraViewPhoto,
        backdrop,
        refreshCameraView,
        handleDeleteCameraOverview,
        listCameraView,
        setListCameraView,
        setSelectedGeozone,
    } = useCameraDetailContext();

    const popupDeleteViewOverview = usePopUp();
    const queryClient = useQueryClient();
    const popUpWarningEnableExtraPhoto = usePopUp();

    const [loadingGeozones, setLoadingGeozones] = useState(true);
    const [isEnable, setIsEnable] = useState<boolean>(false);

    const prevGeoZonePointsCameraPTZ: GeoZoneCoordinates[][] =
        (selectedCameraView?.GeoZones?.map((gz) => {
            return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
        }) as GeoZoneCoordinates[][]) ?? [];

    const prevGeoZonesCameraPTZ = getGeoZone(scaleFullCameraPTZ, prevGeoZonePointsCameraPTZ);
    const scaledPrevGeoZoneCameraPTZ = getGeoZone(scaleNotFullCameraPTZ, prevGeoZonePointsCameraPTZ);

    const handleRefreshCameraView = async () => {
        backdrop.setTrue();

        let cameraViewToSend = { ...selectedCameraView! };

        if (!selectedCameraView!.PresetId && listsViewPreset.length > 0) {
            const presetName = selectedCameraView!.CameraViewName;
            const matchedPreset = listsViewPreset.find((preset) => preset.PresetName === presetName);

            if (matchedPreset) {
                cameraViewToSend.PresetId = matchedPreset.GotoPointId;
            }
        }
        await handleUpdateCameraViewPhoto({
            CameraView: cameraViewToSend,
            PTZCommand: {
                authenticate: {
                    password: camera.PassWord,
                    username: camera.UserName,
                },
                ip: camera.IP,
            },
        })
            .then((res) => {
                refreshCameraView(res);
                pushSuccess(`Refresh ${selectedCameraView?.CameraViewName} successfully`);
            })
            .catch((err) => {
                pushError(err.response?.data?.message?.response);
            })
            .finally(() => {
                backdrop.setFalse();
            });
    };

    const deleteViewOverview = async () => {
        backdrop.setTrue();

        await handleDeleteCameraOverview(selectedCameraView?.Id!);

        popupDeleteViewOverview.onClose();
        pushSuccess(`Delete ${selectedCameraView?.CameraViewName} successfully`);
        queryClient.invalidateQueries(['patrolTimeForecast']);
        queryClient.invalidateQueries(['trailAuditEvent']);

        backdrop.setFalse();
    };

    const handleGetListGeoZoneByView = async (_filter: IGetGeoZoneProps) => {
        setLoadingGeozones(true);
        await geoZoneController
            .listGeoZoneByCameraView(_filter)
            .then((res) => {
                setSelectedCameraView((prev) => ({ ...prev!, GeoZones: res.rows }));
                setListCameraView((prevList) => {
                    const updatedRows = prevList.rows.map((view) => {
                        if (!view?.GeoZones) return view;

                        return {
                            ...view,
                            GeoZones: view.GeoZones.map((gz) => {
                                if (gz?.CameraViewDetail?.Id === _filter?.filter?.CameraViewId) {
                                    return {
                                        ...gz,
                                        CameraViewDetail: {
                                            ...(gz?.CameraViewDetail as any),
                                            GeoZones: res?.rows,
                                        },
                                    };
                                }
                                return gz;
                            }),
                        };
                    });

                    return {
                        ...prevList,
                        rows: updatedRows,
                    };
                });
            })
            .finally(() => {
                setLoadingGeozones(false);
            });
    };

    const enableExtraPhoto = async (isEnable: boolean) => {
        backdrop.setTrue();

        await geoZoneController
            .upsertGeoZoneOverview({
                Id: selectedGeozone?.Id,
                CameraId: selectedGeozone?.CameraId!,
                PostCaptureInterval: isEnable ? 1 : 0,
                PostCaptureNumber: isEnable ? 1 : 0,
            })
            .then((res) => {
                setSelectedGeozone(res);
                queryClient.invalidateQueries(['trailAuditEvent']);
                setListCameraView((prev) => ({
                    ...prev,
                    rows: prev.rows.map((cameraView) => {
                        const existed = cameraView.GeoZones?.some((gz) => gz.Id === res.Id);

                        if (!existed) return cameraView;

                        return {
                            ...cameraView,
                            GeoZones: cameraView.GeoZones?.map((gz) => (gz.Id === res.Id ? res : gz)),
                        };
                    }),
                }));
            });

        popUpWarningEnableExtraPhoto.onClose();
        pushSuccess(`${isEnable ? 'Enable' : 'Disable'} extra night shot for successfully`);

        backdrop.setFalse();
    };

    useEffect(() => {
        if (
            (selectedCameraView?.GeoZones?.length || 0) === 0 &&
            selectedCameraView?.Id &&
            selectedCameraView.ViewType === CameraViewType.ViewDetail
        ) {
            handleGetListGeoZoneByView({
                filter: {
                    CameraId: Number(camera.Id),
                    CameraViewId: selectedCameraView.Id,
                },
            });
        } else {
            setLoadingGeozones(false);
        }
    }, [selectedCameraView?.Id]);

    useEffect(() => {
        if ((selectedGeozone?.PostCaptureInterval ?? 0) > 0 && (selectedGeozone?.PostCaptureNumber ?? 0) > 0) {
            setIsEnable(true);
        } else {
            setIsEnable(false);
        }
    }, [selectedGeozone]);

    return selectedCameraView ? (
        <Stack key={selectedCameraView?.Id} direction={'row'} justifyContent={'space-between'} width={'100%'}>
            <Stack spacing={2} width={'100%'} borderRadius={2}>
                <Stack spacing={2}>
                    <Stack justifyContent={'space-between'} alignItems={'center'} direction={'row'}>
                        <Stack direction={'row'} spacing={2} justifyContent={'center'}>
                            {/* <Typography variant="h5">Details:</Typography> */}
                            <Typography variant="h5">View: {selectedCameraView?.CameraViewName}</Typography>
                        </Stack>

                        <Stack direction={'row'} spacing={1} display={camera.Deleted ? 'none' : 'flex'}>
                            {selectedCameraView.ViewType === CameraViewType.ViewDetail && (
                                <Tooltip
                                    placement="top"
                                    arrow
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                maxWidth: 440,
                                            },
                                        },
                                    }}
                                    title="Automatically capture an extra photo a few seconds after the first one during nighttime to reduce blur caused by slow focusing"
                                >
                                    <Stack direction={'row'} alignItems="center">
                                        <Typography variant="body1" color={'#85858A'} sx={{ marginRight: 1 }}>
                                            Extra night shot
                                        </Typography>

                                        <IOSSwitch
                                            iosSwitchProps={{
                                                checked: isEnable,
                                                onChange(_) {
                                                    popUpWarningEnableExtraPhoto.setTrue();
                                                },
                                            }}
                                        />
                                    </Stack>
                                </Tooltip>
                            )}

                            <Button size="small" variant="contained" onClick={configView}>
                                Config view
                            </Button>

                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    selectedCameraView.ViewType === CameraViewType.ViewOverview
                                        ? navigate(`add/${typeTextMap[CameraType.PTZ]}`, {
                                              state: {
                                                  cameraView: selectedCameraView,
                                                  listCameraView: listCameraView.rows,
                                              },
                                              replace: true,
                                          })
                                        : navigate(`overview/${selectedGeozone?.Id}/add`, {
                                              state: {
                                                  geoZonesItem: selectedGeozone,
                                                  cameraView: selectedCameraView,
                                              },
                                              replace: true,
                                          });
                                }}
                            >
                                Add geo-zones
                            </Button>

                            {selectedCameraView.ViewType === CameraViewType.ViewOverview && (
                                <Button
                                    size="small"
                                    variant="errorOutlined"
                                    onClick={() => popupDeleteViewOverview.setTrue()}
                                    sx={{
                                        padding: '4px 16px',
                                    }}
                                >
                                    Delete
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                    {selectedCameraView ? (
                        <HistoriesPhotoCameraPTZ
                            handleMouseMove={(e) =>
                                handleMouseMove(e, scaledPrevGeoZoneCameraPTZ, selectedCameraView.Id!)
                            }
                            cameraView={selectedCameraView}
                            prevGeoZonesCameraPTZ={prevGeoZonesCameraPTZ}
                            scaledPrevGeoZoneCameraPTZ={scaledPrevGeoZoneCameraPTZ}
                            refreshCameraView={handleRefreshCameraView}
                            loadGeozones={loadingGeozones}
                        />
                    ) : (
                        <ListNoData
                            sx={{
                                mt: 2,
                                minHeight: '500px',
                                backgroundColor: color.white,
                                border: 'none',
                            }}
                        />
                    )}
                </Stack>
            </Stack>

            <PopUpWarning
                {...popupDeleteViewOverview}
                onConfirm={deleteViewOverview}
                title="Confirm"
                message={'Are you sure you want to delete this overview?'}
                isLoading={isLoadingSave}
                onClose={() => {
                    popupDeleteViewOverview.onClose();
                }}
            />

            <PopUpWarning
                {...popUpWarningEnableExtraPhoto}
                onConfirm={() => enableExtraPhoto(!isEnable)}
                title="Confirm"
                message={`Are you sure you want to change the setting?\n ${
                    !isEnable
                        ? `When enabled, the camera will take extra night shot after the initial capture.`
                        : 'When disabled, the camera will not take extra night shot after the initial capture.'
                }`}
                isLoading={isLoadingSave}
                onClose={() => {
                    popUpWarningEnableExtraPhoto.onClose();
                }}
            />
        </Stack>
    ) : (
        <Stack maxHeight={'600px'}>
            <ListNoData />
        </Stack>
    );
}
